import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
    sendChatMessage,
    getConversation,
    getConversations,
    exportProjectArticles,
    exportAllArticles,
    exportQuickWins,
    getArticlesCount,
    updateConversation,
    deleteConversation,
    createProject,
    updateProject,
    getTasksForProject,
    createQuickWinSocialMedia,
    createQuickWinMetaData,
    createQuickWinAdCopy,
    createQuickWinKeywordResearch,
    createQuickWinEmailNewsletter,
    createBatchNewsletters,
    createQuickWinContentCalendar,
    getMasterplanStreams,
    selectImagesForTask,
    getLandingPages,
    getBrandGuidesByProject,
    repurposeArticle,
    Message,
    Conversation,
    getProjects,
    Project,
    API_BASE_URL
} from '../services/api';
import ProgressModal from './ProgressModal';
import CreateProjectModal from './CreateProjectModal';
import MasterplanWizard from './MasterplanWizard';
import LandingPageModal from './LandingPageModal';
import BrandGuidesList from './BrandGuidesList';
import BrandGuideCreator from './BrandGuideCreator';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { User, Bot, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Paperclip, FolderOpen, Plus, Globe } from 'lucide-react';
import './ChatInterface.css';

// Helper function to read file content
const readFileContent = async (file: File): Promise<string> => {
    const name = file.name.toLowerCase();
    // Binary formats (docx, pdf) must be decoded server-side — FileReader.readAsText
    // on a zip-based docx returns garbage that the LLM rejects as unreadable.
    if (name.endsWith('.docx') || name.endsWith('.pdf')) {
        const form = new FormData();
        form.append('file', file);
        const token = localStorage.getItem('access_token');
        const resp = await fetch('/api/chat/extract-document', {
            method: 'POST',
            body: form,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!resp.ok) {
            const detail = await resp.text().catch(() => '');
            throw new Error(`Could not read ${file.name}: ${detail || resp.statusText}`);
        }
        const data = await resp.json();
        return data.content as string;
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
    });
};

interface ChatInterfaceProps {
    selectedProjectId?: string;
    onProjectSelect?: (projectId: string | undefined) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ selectedProjectId: propProjectId, onProjectSelect }) => {
    const { conversationId } = useParams<{ conversationId?: string }>();
    const navigate = useNavigate();

    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(conversationId);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    // Use prop if available, otherwise fallback to local state
    const [localProjectId, setLocalProjectId] = useState<string | undefined>();
    
    const selectedProjectId = propProjectId !== undefined ? propProjectId : localProjectId;
    const setSelectedProjectId = (id: string | undefined) => {
        if (onProjectSelect) {
            onProjectSelect(id);
        } else {
            setLocalProjectId(id);
        }
    };
    
    const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed since we have global sidebar
    const [executingTasks, setExecutingTasks] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [articlesCount, setArticlesCount] = useState<number>(0);
    const [showProgress, setShowProgress] = useState(false);
    const [progressData, setProgressData] = useState({
        currentTask: '',
        currentIndex: 0,
        totalTasks: 0
    });
    const [currentTaskType, setCurrentTaskType] = useState<'article' | 'quick_win' | 'general'>('general');
    const [currentQuickWinType, setCurrentQuickWinType] = useState<string | null>(null);
    const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
    const [projectSelectorOpen, setProjectSelectorOpen] = useState(false);
    const [editingProjectDescription, setEditingProjectDescription] = useState(false);
    const [projectDescriptionValue, setProjectDescriptionValue] = useState('');
    const [editingProjectName, setEditingProjectName] = useState(false);
    const [projectNameValue, setProjectNameValue] = useState('');
    const [selectedQuickWin, setSelectedQuickWin] = useState('');
    const [generatingQuickWin, setGeneratingQuickWin] = useState(false);
    const [masterplanStreams, setMasterplanStreams] = useState<string[]>([]);
    const [hasMasterplan, setHasMasterplan] = useState(false);
    const [exportFormat, setExportFormat] = useState<'markdown' | 'html'>('markdown');
    const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
    const [showStreamSelector, setShowStreamSelector] = useState(false);
    const [quickWinExportType, setQuickWinExportType] = useState<string>('all');
    const [quickWinExportFormat, setQuickWinExportFormat] = useState<'markdown' | 'html'>('markdown');
    const [includeMetadata, setIncludeMetadata] = useState<boolean>(false);
    const [quickWinsCount, setQuickWinsCount] = useState(0);
    const [totalQuickWinsCount, setTotalQuickWinsCount] = useState(0);  // Track total to show/hide section
    const [pendingQuickWins, setPendingQuickWins] = useState<any>(null);
    const [showPendingPrompt, setShowPendingPrompt] = useState(false);
    const [showMasterplanWizard, setShowMasterplanWizard] = useState(false);
    const [groundingEnabled, setGroundingEnabled] = useState(false);
    const [selectingImages, setSelectingImages] = useState(false);
    const [imageSelectionProgress, setImageSelectionProgress] = useState({ current: 0, total: 0, currentArticle: '' });
    const [cancelImageSelection, setCancelImageSelection] = useState(false);
    const [landingPagesCount, setLandingPagesCount] = useState(0);
    const [showLandingPageModal, setShowLandingPageModal] = useState(false);
    const [landingPageModalMode, setLandingPageModalMode] = useState<'create' | 'list'>('list');
    const [brandGuidesCount, setBrandGuidesCount] = useState(0);
    const [showBrandGuideModal, setShowBrandGuideModal] = useState(false);
    const [brandGuideModalMode, setBrandGuideModalMode] = useState<'create' | 'list'>('list');

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editInputRef = useRef<HTMLInputElement>(null);
    const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
    const projectNameInputRef = useRef<HTMLInputElement>(null);

    // Scroll the messages container to its bottom when messages change.
    // We deliberately do NOT use scrollIntoView() here — it walks up every
    // scrollable ancestor and scrolls each one until the target is visible,
    // which on chat switches with long histories ends up scrolling the outer
    // app-layout/content-area too, clipping the header off the top of the UI.
    useEffect(() => {
        const c = messagesContainerRef.current;
        if (c) c.scrollTop = c.scrollHeight;
    }, [messages]);

    // Load conversations and projects on mount
    useEffect(() => {
        loadConversations();
        loadProjects();
    }, []);

    // Sync URL param → state (handles navigation from the global Layout sidebar which uses <Link>)
    useEffect(() => {
        if (conversationId && conversationId !== currentConversationId) {
            setCurrentConversationId(conversationId);
        }
    }, [conversationId]);

    // Load conversation messages when conversationId changes
    useEffect(() => {
        if (currentConversationId) {
            loadConversation(currentConversationId);
        }
    }, [currentConversationId]);

    // Load articles count when selected project changes
    useEffect(() => {
        loadArticlesCount();
        loadMasterplanStreams();
        loadQuickWinsCount();
        checkPendingQuickWins();
        loadLandingPagesCount();
        loadBrandGuidesCount();
    }, [selectedProjectId]);

    // Reload quick wins count when export type changes
    useEffect(() => {
        loadQuickWinsCount();
    }, [quickWinExportType]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (editingConversationId && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingConversationId]);

    // Restore image selection progress on page load
    useEffect(() => {
        const restoreImageSelectionProgress = async () => {
            if (!selectedProjectId) return;

            const progressKey = `imageSelection_${selectedProjectId}`;
            const savedProgress = localStorage.getItem(progressKey);

            if (!savedProgress) return;

            try {
                const { taskIds, currentIndex, completed } = JSON.parse(savedProgress);

                // Check if there are still tasks to process
                if (currentIndex >= taskIds.length) {
                    localStorage.removeItem(progressKey);
                    return;
                }

                // Restore state
                setSelectingImages(true);
                setImageSelectionProgress({
                    current: completed || 0,
                    total: taskIds.length,
                    currentArticle: 'Resuming...'
                });

                // Resume processing from where we left off
                const tasks = await getTasksForProject(selectedProjectId);
                const remainingTaskIds = taskIds.slice(currentIndex || 0);
                let resumeCompleted = completed || 0;

                for (let i = 0; i < remainingTaskIds.length; i++) {
                    // Check for cancellation
                    if (cancelImageSelection) {
                        console.log('Image selection cancelled by user');
                        break;
                    }

                    const taskId = remainingTaskIds[i];
                    const task = tasks.find(t => t.task_id === taskId);

                    if (!task) continue;

                    const articleTitle = task.name.replace('Generate article:', '').trim();

                    setImageSelectionProgress({
                        current: resumeCompleted,
                        total: taskIds.length,
                        currentArticle: articleTitle
                    });

                    try {
                        await selectImagesForTask(taskId, false);
                        resumeCompleted++;

                        setImageSelectionProgress({
                            current: resumeCompleted,
                            total: taskIds.length,
                            currentArticle: articleTitle
                        });

                        // Update localStorage
                        localStorage.setItem(progressKey, JSON.stringify({
                            taskIds,
                            currentIndex: (currentIndex || 0) + i + 1,
                            completed: resumeCompleted,
                            startedAt: Date.now()
                        }));
                    } catch (error) {
                        console.error(`Failed to select images for task ${taskId}:`, error);
                    }
                }

                // Done - clear state
                localStorage.removeItem(progressKey);
                setSelectingImages(false);
                setImageSelectionProgress({ current: 0, total: 0, currentArticle: '' });

                alert(`Image selection complete! Processed ${resumeCompleted}/${taskIds.length} articles.`);

            } catch (error) {
                console.error('Failed to restore image selection:', error);
                localStorage.removeItem(progressKey);
                setSelectingImages(false);
            }
        };

        restoreImageSelectionProgress();
    }, [selectedProjectId]);

    // Focus textarea when entering project description edit mode
    useEffect(() => {
        if (editingProjectDescription && descriptionInputRef.current) {
            descriptionInputRef.current.focus();
            descriptionInputRef.current.select();
        }
    }, [editingProjectDescription]);

    // Focus input when entering project name edit mode
    useEffect(() => {
        if (editingProjectName && projectNameInputRef.current) {
            projectNameInputRef.current.focus();
            projectNameInputRef.current.select();
        }
    }, [editingProjectName]);

    const loadProjects = async () => {
        try {
            console.log('Loading projects...');
            const projectsData = await getProjects();
            console.log('Projects loaded successfully:', projectsData);
            setProjects(projectsData);
        } catch (error: any) {
            console.error('Failed to load projects:', error);
            console.error('Error details:', {
                message: error.message,
                code: error.code,
                name: error.name,
                request: error.request,
                response: error.response
            });
        }
    };

    const loadConversations = async () => {
        try {
            const conversationsData = await getConversations();
            setConversations(conversationsData);
        } catch (error) {
            console.error('Failed to load conversations:', error);
        }
    };

    const loadConversation = async (convId: string) => {
        try {
            const conversation = await getConversation(convId);
            setMessages(conversation.messages);
            setSelectedProjectId(conversation.project_id);
            // Restore the per-conversation "Research live sources" choice. The chat UI
            // remounts after the first message (route change to /chat/<id>), which would
            // otherwise reset the toggle to off; the backend persists the choice on the
            // conversation so we can re-hydrate it here (survives remount and reload).
            const storedGrounding = conversation.metadata?.grounding_mode;
            if (storedGrounding !== undefined && storedGrounding !== null) {
                setGroundingEnabled(storedGrounding !== 'off');
            }
        } catch (error) {
            console.error('Failed to load conversation:', error);
        }
    };

    const loadArticlesCount = async () => {
        try {
            const result = await getArticlesCount(selectedProjectId);
            setArticlesCount(result.count);
        } catch (error) {
            console.error('Failed to load articles count:', error);
            setArticlesCount(0);
        }
    };

    const loadQuickWinsCount = async () => {
        if (!selectedProjectId) {
            setQuickWinsCount(0);
            setTotalQuickWinsCount(0);
            return;
        }

        try {
            const tasks = await getTasksForProject(selectedProjectId);
            const allQuickWins = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                t.input_data &&
                typeof t.input_data === 'object' &&
                'quick_win_type' in t.input_data
            );

            // Set total count (for determining if section should be visible)
            setTotalQuickWinsCount(allQuickWins.length);

            // Map frontend quick_win_type values to database values
            // Frontend uses: social_media, meta_data, ad_copy, keyword_research
            // Database uses: social_media_posts, seo_metadata, ad_copy_variations, keyword_research_data
            const quickWinTypeMapping: Record<string, string> = {
                'social_media': 'social_media_posts',
                'meta_data': 'seo_metadata',
                'ad_copy': 'ad_copy_variations',
                'keyword_research': 'keyword_research_data'
            };

            // Filter by quick win type if not 'all'
            let quickWins = allQuickWins;
            if (quickWinExportType !== 'all') {
                const dbQuickWinType = quickWinTypeMapping[quickWinExportType] || quickWinExportType;
                quickWins = allQuickWins.filter(t =>
                    (t.input_data as any).quick_win_type === dbQuickWinType
                );
            }

            setQuickWinsCount(quickWins.length);
        } catch (error) {
            console.error('Failed to load quick wins count:', error);
            setQuickWinsCount(0);
            setTotalQuickWinsCount(0);
        }
    };

    const loadLandingPagesCount = async () => {
        if (!selectedProjectId) {
            setLandingPagesCount(0);
            return;
        }

        try {
            const landingPages = await getLandingPages(selectedProjectId);
            setLandingPagesCount(landingPages.length);
        } catch (error) {
            console.error('Failed to load landing pages count:', error);
            setLandingPagesCount(0);
        }
    };

    const loadBrandGuidesCount = async () => {
        if (!selectedProjectId) {
            setBrandGuidesCount(0);
            return;
        }

        try {
            const brandGuides = await getBrandGuidesByProject(selectedProjectId);
            setBrandGuidesCount(brandGuides.length);
        } catch (error) {
            console.error('Failed to load brand guides count:', error);
            setBrandGuidesCount(0);
        }
    };

    const checkPendingQuickWins = async () => {
        if (!selectedProjectId) {
            setPendingQuickWins(null);
            setShowPendingPrompt(false);
            return;
        }

        try {
            const pqwToken = localStorage.getItem('access_token') || '';
            const response = await fetch(`${API_BASE_URL}/projects/${selectedProjectId}/pending_quick_wins`, {
                headers: pqwToken ? { Authorization: `Bearer ${pqwToken}` } : {},
            });
            const data = await response.json();

            if (data.has_pending) {
                setPendingQuickWins(data);
                setShowPendingPrompt(true);
            } else {
                setPendingQuickWins(null);
                setShowPendingPrompt(false);
            }
        } catch (error) {
            console.error('Error checking pending quick wins:', error);
            setPendingQuickWins(null);
            setShowPendingPrompt(false);
        }
    };

    const loadMasterplanStreams = async () => {
        if (!selectedProjectId) {
            setMasterplanStreams([]);
            setHasMasterplan(false);
            return;
        }

        try {
            const result = await getMasterplanStreams(selectedProjectId);
            setMasterplanStreams(result.streams);
            setHasMasterplan(result.has_masterplan);
        } catch (error) {
            console.error('Failed to load masterplan streams:', error);
            setMasterplanStreams([]);
            setHasMasterplan(false);
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = async () => {
        if ((!inputValue.trim() && !selectedFile) || isLoading) return;

        const userMessage = inputValue.trim() || '[Uploaded file]';
        setInputValue('');
        setIsLoading(true);

        // Read file content if a file is selected
        let attachments: Array<{ filename: string; content: string }> | undefined;
        if (selectedFile) {
            try {
                const fileContent = await readFileContent(selectedFile);
                attachments = [{
                    filename: selectedFile.name,
                    content: fileContent
                }];
            } catch (error) {
                console.error('Failed to read file:', error);
                alert('Failed to read file');
                setIsLoading(false);
                return;
            }
        }

        // Add user message to UI immediately
        const tempUserMessage: Message = {
            message_id: 'temp-' + Date.now(),
            conversation_id: currentConversationId || 'new',
            role: 'user',
            content: userMessage + (selectedFile ? ` [Attached: ${selectedFile.name}]` : ''),
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, tempUserMessage]);

        // Clear file selection
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        try {
            const response = await sendChatMessage({
                message: userMessage,
                conversation_id: currentConversationId,
                project_id: selectedProjectId,
                attachments: attachments,
                grounding_mode: groundingEnabled ? 'auto' : 'off',
            });

            // Update conversation ID if this was a new conversation
            if (!currentConversationId) {
                setCurrentConversationId(response.conversation_id);
                navigate(`/chat/${response.conversation_id}`);
            }

            // Add assistant message
            const assistantMessage: Message = {
                message_id: response.message_id || 'temp-' + Date.now(),
                conversation_id: response.conversation_id,
                role: 'assistant',
                content: response.content,
                created_at: new Date().toISOString(),
                metadata: { tasks_created: response.tasks_created },
            };

            setMessages(prev => [...prev.filter(m => m.message_id !== tempUserMessage.message_id), tempUserMessage, assistantMessage]);

            // Check if assistant response mentions masterplan wizard
            const masterplanTriggers = [
                'start the masterplan wizard',
                'masterplan wizard',
                'create a comprehensive content masterplan'
            ];

            if (masterplanTriggers.some(trigger => response.content.toLowerCase().includes(trigger))) {
                // If we have a project selected, show the wizard automatically
                if (selectedProjectId) {
                    setTimeout(() => {
                        setShowMasterplanWizard(true);
                    }, 500); // Small delay to let the message render first
                }
            }

            // Reload conversations list
            loadConversations();
        } catch (error) {
            console.error('Failed to send message:', error);
            const errorMessage: Message = {
                message_id: 'error-' + Date.now(),
                conversation_id: currentConversationId || 'new',
                role: 'assistant',
                content: `Error: ${error instanceof Error ? error.message : 'Failed to send message'}`,
                created_at: new Date().toISOString(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleExecuteTasks = async () => {
        if (!currentConversationId || executingTasks) return;

        setExecutingTasks(true);
        setShowProgress(true);
        setCurrentTaskType('article');  // Conversation tasks are articles by default
        setCurrentQuickWinType(null);   // Reset; SSE will set per-task
        setProgressData({
            currentTask: 'Initializing...',
            currentIndex: 0,
            totalTasks: 0
        });

        // Track quick-win type locally — closure captures of React state are stale
        // by the time the SSE 'complete' event arrives. We need a value the
        // handler can read synchronously.
        let lastQuickWinType: string | null = null;
        let sawArticleTask = false;
        // Per-task type + name, populated from each task_complete event so we
        // can render a context-aware summary at the end.
        const completedTasks: Array<{ task_id: string; task_name: string; quick_win_type: string | null }> = [];
        try {
            const token = localStorage.getItem('access_token') || '';
            const eventSource = new EventSource(
                `${API_BASE_URL}/chat/${currentConversationId}/execute_tasks_stream/?token=${encodeURIComponent(token)}`
            );

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);

                if (data.status === 'started') {
                    setProgressData({
                        currentTask: 'Starting task execution...',
                        currentIndex: 0,
                        totalTasks: data.total
                    });
                } else if (data.status === 'processing') {
                    setProgressData({
                        currentTask: data.task_name,
                        currentIndex: data.current - 1,  // N-1 completed; task N is starting
                        totalTasks: data.total
                    });
                    if (data.quick_win_type !== undefined) {
                        setCurrentQuickWinType(data.quick_win_type);
                        lastQuickWinType = data.quick_win_type ?? null;
                        if (!data.quick_win_type) sawArticleTask = true;
                    } else {
                        sawArticleTask = true;
                    }
                } else if (data.status === 'task_complete') {
                    // Task N finished — now N are completed
                    setProgressData({
                        currentTask: data.task_name,
                        currentIndex: data.current,
                        totalTasks: data.total
                    });
                    completedTasks.push({
                        task_id: data.task_id,
                        task_name: data.task_name || 'Untitled',
                        quick_win_type: data.quick_win_type ?? null,
                    });
                } else if (data.status === 'task_error') {
                    console.error(`Error on task ${data.task_name}:`, data.error);
                    setProgressData({
                        currentTask: `Error: ${data.task_name}`,
                        currentIndex: data.current,
                        totalTasks: data.total
                    });
                } else if (data.status === 'no_pending') {
                    eventSource.close();
                    setShowProgress(false);
                    setExecutingTasks(false);
                    setMessages(prev => [...prev, {
                        message_id: `temp-${Date.now()}`,
                        conversation_id: currentConversationId!,
                        role: 'assistant' as const,
                        content: 'No pending tasks found. Tasks may have already been executed or are still being created — please try again in a moment.',
                        created_at: new Date().toISOString()
                    }]);
                } else if (data.status === 'complete') {
                    eventSource.close();
                    setShowProgress(false);
                    setExecutingTasks(false);

                    const completed = data.completed ?? 0;
                    const failed = data.failed ?? 0;

                    // Group completed tasks by quick_win_type so the summary
                    // can produce one rich message per content type with links
                    // to both the catalogue module and (for single items) the
                    // direct preview.
                    if (completedTasks.length > 0) {
                        const groups = new Map<string, typeof completedTasks>();
                        completedTasks.forEach(t => {
                            const key = t.quick_win_type || 'article';
                            if (!groups.has(key)) groups.set(key, []);
                            groups.get(key)!.push(t);
                        });
                        groups.forEach((tasks, type) => {
                            const payload = { type, tasks, failed: 0 };
                            setMessages(prev => [...prev, {
                                message_id: `gen-complete-${type}-${Date.now()}`,
                                conversation_id: currentConversationId!,
                                role: 'assistant' as const,
                                content: `__GENERATION_COMPLETE__:${JSON.stringify(payload)}`,
                                created_at: new Date().toISOString(),
                            }]);
                        });
                    }

                    if (failed > 0) {
                        setMessages(prev => [...prev, {
                            message_id: `gen-failed-${Date.now()}`,
                            conversation_id: currentConversationId!,
                            role: 'assistant' as const,
                            content: `${failed} task${failed === 1 ? '' : 's'} failed during generation.`,
                            created_at: new Date().toISOString(),
                        }]);
                    } else if (completed === 0) {
                        setMessages(prev => [...prev, {
                            message_id: `gen-none-${Date.now()}`,
                            conversation_id: currentConversationId!,
                            role: 'assistant' as const,
                            content: 'No tasks completed.',
                            created_at: new Date().toISOString(),
                        }]);
                    }

                    loadArticlesCount();

                    // Suggest repurposing only when actual articles were generated.
                    // Quick-win batches (emails, social, SEO, ad copy, etc.) ARE the
                    // repurposed artefacts — offering to repurpose them again makes
                    // no sense. Use the local tracker (not React state) — by this
                    // point the state-setter closure is stale.
                    const wasArticleBatch = sawArticleTask && !lastQuickWinType;
                    // Only the article task IDs generated in THIS turn — never the whole
                    // project back-catalogue. Repurposing every completed article in the
                    // project (145+ for established clients) loops for hours and never
                    // visibly finishes.
                    const justGeneratedArticleIds = completedTasks
                        .filter(t => !t.quick_win_type)
                        .map(t => t.task_id)
                        .filter(Boolean);
                    if (completed > 0 && selectedProjectId && wasArticleBatch && justGeneratedArticleIds.length > 0) {
                        setMessages(prev => [...prev, {
                            message_id: `repurpose-suggest-${Date.now()}`,
                            conversation_id: currentConversationId!,
                            role: 'assistant' as const,
                            content: `__REPURPOSE_SUGGESTION__:${selectedProjectId}|${justGeneratedArticleIds.join(',')}`,
                            created_at: new Date().toISOString()
                        }]);
                    }
                } else if (data.status === 'error') {
                    eventSource.close();
                    setShowProgress(false);
                    setExecutingTasks(false);

                    // Add error to chat
                    setMessages(prev => [...prev, {
                        message_id: `temp-${Date.now()}`,
                        conversation_id: currentConversationId!,
                        role: 'assistant' as const,
                        content: `✗ Task execution error: ${data.message}`,
                        created_at: new Date().toISOString()
                    }]);

                    alert(`Error: ${data.message}`);
                }
            };

            eventSource.onerror = (error) => {
                console.error('EventSource error:', error);
                eventSource.close();
                setShowProgress(false);
                setExecutingTasks(false);
                alert('Connection error during task execution');
            };

        } catch (error) {
            console.error('Failed to execute tasks:', error);
            alert('Failed to execute tasks');
            setShowProgress(false);
            setExecutingTasks(false);
        }
    };

    const handleExecutePendingQuickWins = async () => {
        if (!selectedProjectId || executingTasks) return;

        setExecutingTasks(true);
        setShowProgress(true);
        setCurrentTaskType('quick_win');  // Pending quick wins
        setProgressData({
            currentTask: 'Initializing...',
            currentIndex: 0,
            totalTasks: 0
        });

        try {
            const qwToken = localStorage.getItem('access_token') || '';
            const eventSource = new EventSource(
                `${API_BASE_URL}/projects/${selectedProjectId}/execute_pending_quick_wins_stream/?token=${encodeURIComponent(qwToken)}`
            );

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);

                if (data.status === 'started') {
                    setProgressData({
                        currentTask: 'Starting task execution...',
                        currentIndex: 0,
                        totalTasks: data.total
                    });
                } else if (data.status === 'processing') {
                    setProgressData({
                        currentTask: data.task_name,
                        currentIndex: data.current - 1,  // N-1 completed; task N is starting
                        totalTasks: data.total
                    });
                    if (data.quick_win_type !== undefined) {
                        setCurrentQuickWinType(data.quick_win_type);
                    }
                } else if (data.status === 'task_complete') {
                    // Task N finished — now N are completed
                    setProgressData({
                        currentTask: data.task_name,
                        currentIndex: data.current,
                        totalTasks: data.total
                    });
                } else if (data.status === 'task_error') {
                    console.error(`Error on task ${data.task_name}:`, data.error);
                    setProgressData({
                        currentTask: `Error: ${data.task_name}`,
                        currentIndex: data.current,
                        totalTasks: data.total
                    });
                } else if (data.status === 'complete') {
                    eventSource.close();
                    setShowProgress(false);
                    setExecutingTasks(false);

                    const completed = data.completed ?? 0;
                    const total = data.total ?? 0;
                    const summaryMessage = `✓ Quick wins generation completed: ${completed} of ${total} tasks succeeded${data.failed > 0 ? `, ${data.failed} failed` : ''}.`;

                    alert(`Completed ${completed} of ${total} tasks successfully!`);

                    // Reload counts and check for remaining pending tasks
                    loadQuickWinsCount();
                    setTimeout(() => checkPendingQuickWins(), 1000);
                } else if (data.status === 'error') {
                    eventSource.close();
                    setShowProgress(false);
                    setExecutingTasks(false);
                    alert(`Error: ${data.message}`);
                }
            };

            eventSource.onerror = (error) => {
                console.error('EventSource error:', error);
                eventSource.close();
                setShowProgress(false);
                setExecutingTasks(false);
                alert('Connection lost. Please check the server and try again.');
            };

        } catch (error) {
            console.error('Failed to execute pending quick wins:', error);
            setShowProgress(false);
            setExecutingTasks(false);
            alert('Failed to start task execution.');
        }
    };

    const handleExportArticles = async () => {
        try {
            let blob: Blob;
            let filename: string;

            if (selectedProjectId) {
                // Export articles for selected project
                blob = await exportProjectArticles(selectedProjectId, 'completed', 'zip', exportFormat, includeMetadata);
                const project = projects.find(p => p.project_id === selectedProjectId);
                const formatSuffix = exportFormat === 'html' ? '_html' : '';
                filename = `${project?.name.replace(/\s+/g, '_')}_articles${formatSuffix}.zip` || 'articles.zip';
            } else {
                // Export all articles
                blob = await exportAllArticles();
                filename = 'all_articles.zip';
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export articles:', error);
            alert('Failed to export articles. Make sure there are completed articles to export.');
        }
    };

    const handleExportQuickWins = async () => {
        if (!selectedProjectId) {
            alert('Please select a project first.');
            return;
        }

        try {
            const blob = await exportQuickWins(
                selectedProjectId,
                quickWinExportType,
                'completed',
                'zip',
                quickWinExportFormat,
                includeMetadata
            );

            const project = projects.find(p => p.project_id === selectedProjectId);
            const typeLabel = quickWinExportType === 'all' ? 'quick_wins' : quickWinExportType;
            const formatSuffix = quickWinExportFormat === 'html' ? '_html' : '';
            const filename = `${project?.name.replace(/\s+/g, '_')}_${typeLabel}${formatSuffix}.zip` || `${typeLabel}.zip`;

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export quick wins:', error);
            alert('Failed to export quick wins. Make sure there are completed quick wins to export.');
        }
    };

    const handleSelectImagesForAll = async () => {
        if (!selectedProjectId || selectingImages) return;

        try {
            // Get all tasks for the project
            const tasks = await getTasksForProject(selectedProjectId);

            // Filter for completed article tasks without images
            const articlesWithoutImages = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                t.name.includes('Generate article:') &&
                (!t.output_data || !t.output_data.images)
            );

            if (articlesWithoutImages.length === 0) {
                alert('All articles already have images!');
                return;
            }

            const proceed = window.confirm(
                `Select images for ${articlesWithoutImages.length} article${articlesWithoutImages.length !== 1 ? 's' : ''} without images?\n\n` +
                `This will use the Pexels API and Gemini AI to analyze and select the best images. ` +
                `It may take 30-60 seconds per article.`
            );

            if (!proceed) return;

            console.log('[DEBUG] Starting image selection for', articlesWithoutImages.length, 'articles');
            flushSync(() => {
                setSelectingImages(true);
                setCancelImageSelection(false);
                setImageSelectionProgress({ current: 0, total: articlesWithoutImages.length, currentArticle: '' });
            });
            console.log('[DEBUG] State set - selectingImages:', true, 'total:', articlesWithoutImages.length);

            // Save to localStorage for persistence across refreshes
            const progressKey = `imageSelection_${selectedProjectId}`;
            const taskIds = articlesWithoutImages.map(t => t.task_id);
            localStorage.setItem(progressKey, JSON.stringify({
                taskIds,
                startedAt: Date.now()
            }));

            let completed = 0;

            for (let i = 0; i < articlesWithoutImages.length; i++) {
                // Check for cancellation
                if (cancelImageSelection) {
                    console.log('Image selection cancelled by user');
                    break;
                }

                const task = articlesWithoutImages[i];
                const articleTitle = task.name.replace('Generate article:', '').trim();

                // Update progress with current article
                flushSync(() => {
                    setImageSelectionProgress({
                        current: i,
                        total: articlesWithoutImages.length,
                        currentArticle: articleTitle
                    });
                });

                // Save current progress to localStorage
                localStorage.setItem(progressKey, JSON.stringify({
                    taskIds,
                    currentIndex: i,
                    completed,
                    startedAt: Date.now()
                }));

                try {
                    await selectImagesForTask(task.task_id, false);
                    completed++;

                    // Update progress after completion
                    setImageSelectionProgress({
                        current: completed,
                        total: articlesWithoutImages.length,
                        currentArticle: articleTitle
                    });

                    // Update localStorage with completion
                    localStorage.setItem(progressKey, JSON.stringify({
                        taskIds,
                        currentIndex: i + 1,
                        completed,
                        startedAt: Date.now()
                    }));
                } catch (error) {
                    console.error(`Failed to select images for task ${task.task_id}:`, error);
                }
            }

            // Clear localStorage when done
            localStorage.removeItem(progressKey);

            const message = cancelImageSelection
                ? `Image selection cancelled. Successfully selected images for ${completed}/${articlesWithoutImages.length} articles.`
                : `Successfully selected images for ${completed}/${articlesWithoutImages.length} articles!`;
            alert(message);

            // Refresh article count to trigger UI update
            if (selectedProjectId) {
                const countData = await getArticlesCount(selectedProjectId);
                setArticlesCount(countData.count);
            }
        } catch (error) {
            console.error('Failed to select images:', error);
            alert('Failed to select images. Please try again.');
        } finally {
            setSelectingImages(false);
            setCancelImageSelection(false);
            setImageSelectionProgress({ current: 0, total: 0, currentArticle: '' });

            // Clear localStorage
            if (selectedProjectId) {
                localStorage.removeItem(`imageSelection_${selectedProjectId}`);
            }
        }
    };

    const handleGenerateQuickWin = async () => {
        if (!selectedProjectId || !selectedQuickWin || generatingQuickWin) return;

        setGeneratingQuickWin(true);
        try {
            // Get all tasks for the project
            const tasks = await getTasksForProject(selectedProjectId);
            // Filter for completed tasks with content, but EXCLUDE quick win tasks themselves
            let completedTasks = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                // Exclude tasks that are themselves quick wins (prevent quick wins of quick wins)
                !(t.input_data && typeof t.input_data === 'object' && 'quick_win_type' in t.input_data)
            );

            // Filter by selected streams if any are selected
            if (selectedStreams.length > 0) {
                completedTasks = completedTasks.filter(t => {
                    if (t.input_data && typeof t.input_data === 'object' && 'masterplan_stream' in t.input_data) {
                        const stream = (t.input_data as any).masterplan_stream;
                        // Check if the task's stream matches any selected stream
                        return selectedStreams.some(selectedStream => {
                            // Clean both stream names for comparison
                            const cleanSelected = selectedStream.replace(/^\d+\)\s*/, '').trim();
                            const cleanTask = stream.replace(/^\d+\)\s*/, '').trim();
                            return cleanSelected === cleanTask;
                        });
                    }
                    return false;
                });
            }

            if (completedTasks.length === 0) {
                alert(selectedStreams.length > 0
                    ? 'No completed articles found for the selected stream(s).'
                    : 'No completed articles found for this project.');
                setGeneratingQuickWin(false);
                return;
            }

            // Check for existing quick wins of this type
            const existingQuickWins = tasks.filter(t =>
                t.input_data &&
                typeof t.input_data === 'object' &&
                'quick_win_type' in t.input_data &&
                (t.input_data as any).quick_win_type === selectedQuickWin &&
                (t.input_data as any).source_task_id
            );

            // Find which source articles already have quick wins
            const sourceTasksWithQuickWins = new Set(
                existingQuickWins.map(qw => (qw.input_data as any).source_task_id)
            );

            const articlesWithQuickWins = completedTasks.filter(t =>
                sourceTasksWithQuickWins.has(t.task_id)
            );

            // If there are existing quick wins, ask user what to do
            if (articlesWithQuickWins.length > 0) {
                const quickWinTypeLabel = selectedQuickWin === 'social_media' ? 'Social Media Posts'
                    : selectedQuickWin === 'meta_data' ? 'SEO Metadata'
                    : selectedQuickWin === 'ad_copy' ? 'Ad Copy'
                    : selectedQuickWin === 'keyword_research' ? 'Keyword Research'
                    : selectedQuickWin;

                const message = `Found ${articlesWithQuickWins.length} article(s) that already have ${quickWinTypeLabel} quick wins.\n\nDo you want to:\n- OK: Regenerate all (will create duplicates)\n- Cancel: Skip articles that already have quick wins`;

                const regenerateAll = window.confirm(message);

                if (!regenerateAll) {
                    // Filter out articles that already have quick wins
                    completedTasks = completedTasks.filter(t =>
                        !sourceTasksWithQuickWins.has(t.task_id)
                    );

                    if (completedTasks.length === 0) {
                        alert('All selected articles already have this quick win. No new tasks created.');
                        setGeneratingQuickWin(false);
                        setSelectedQuickWin('');
                        setSelectedStreams([]);
                        setShowStreamSelector(false);
                        return;
                    }
                }
            }

            let createdCount = 0;
            let quickWinTypeLabel = '';
            const createdTaskIds: string[] = [];

            // Determine quick win type label
            if (selectedQuickWin === 'social_media') {
                quickWinTypeLabel = 'Social Media Posts';
            } else if (selectedQuickWin === 'meta_data') {
                quickWinTypeLabel = 'SEO Metadata';
            } else if (selectedQuickWin === 'ad_copy') {
                quickWinTypeLabel = 'Ad Copy';
            } else if (selectedQuickWin === 'keyword_research') {
                quickWinTypeLabel = 'Keyword Research';
            } else if (selectedQuickWin === 'email_newsletter') {
                quickWinTypeLabel = 'Emails & Newsletters';
            } else if (selectedQuickWin === 'content_calendar') {
                quickWinTypeLabel = 'Content Calendar';
            }

            // Generate quick wins based on selection
            if (selectedQuickWin === 'social_media') {
                // Filter out articles that already have social media quick wins
                const existingQuickWins = tasks.filter(t =>
                    t.input_data &&
                    typeof t.input_data === 'object' &&
                    'quick_win_type' in t.input_data &&
                    (t.input_data as any).quick_win_type === 'social_media_posts'
                );

                const sourceTasksWithQuickWins = new Set(
                    existingQuickWins.map(qw => (qw.input_data as any).source_task_id)
                );

                const tasksToProcess = completedTasks.filter(
                    task => !sourceTasksWithQuickWins.has(task.task_id)
                );

                // Show progress modal
                setShowProgress(true);
                setProgressData({
                    currentTask: '',
                    currentIndex: 0,
                    totalTasks: tasksToProcess.length
                });

                for (let i = 0; i < tasksToProcess.length; i++) {
                    const task = tasksToProcess[i];
                    setProgressData({
                        currentTask: `${quickWinTypeLabel}: ${task.name}`,
                        currentIndex: i + 1,
                        totalTasks: tasksToProcess.length
                    });
                    // Use the multichannel repurpose pipeline so the result is
                    // registered on the source article's output_data.multichannel
                    // and surfaces in the MultiChannel module, with a fetched
                    // hero image per article.
                    const result = await repurposeArticle({
                        task_id: task.task_id,
                        project_id: selectedProjectId!,
                        generate_email_drip: false,
                        auto_fetch_image: true,
                    });
                    if (result?.task_ids?.social_posts) {
                        createdTaskIds.push(result.task_ids.social_posts);
                    }
                    createdCount++;
                }

                setShowProgress(false);
            } else if (selectedQuickWin === 'meta_data') {
                // Filter out articles that already have meta data quick wins
                const existingQuickWins = tasks.filter(t =>
                    t.input_data &&
                    typeof t.input_data === 'object' &&
                    'quick_win_type' in t.input_data &&
                    (t.input_data as any).quick_win_type === 'meta_data'
                );

                const sourceTasksWithQuickWins = new Set(
                    existingQuickWins.map(qw => (qw.input_data as any).source_task_id)
                );

                const tasksToProcess = completedTasks.filter(
                    task => !sourceTasksWithQuickWins.has(task.task_id)
                );

                // Show progress modal
                setShowProgress(true);
                setProgressData({
                    currentTask: '',
                    currentIndex: 0,
                    totalTasks: tasksToProcess.length
                });

                for (let i = 0; i < tasksToProcess.length; i++) {
                    const task = tasksToProcess[i];
                    setProgressData({
                        currentTask: `${quickWinTypeLabel}: ${task.name}`,
                        currentIndex: i + 1,
                        totalTasks: tasksToProcess.length
                    });
                    const newTask = await createQuickWinMetaData(task.task_id);
                    createdTaskIds.push(newTask.task_id);
                    createdCount++;
                }

                setShowProgress(false);
            } else if (selectedQuickWin === 'ad_copy') {
                // Filter out articles that already have ad copy quick wins
                const existingQuickWins = tasks.filter(t =>
                    t.input_data &&
                    typeof t.input_data === 'object' &&
                    'quick_win_type' in t.input_data &&
                    (t.input_data as any).quick_win_type === 'ad_copy_variations'
                );

                const sourceTasksWithQuickWins = new Set(
                    existingQuickWins.map(qw => (qw.input_data as any).source_task_id)
                );

                const tasksToProcess = completedTasks.filter(
                    task => !sourceTasksWithQuickWins.has(task.task_id)
                );

                // Show progress modal
                setShowProgress(true);
                setProgressData({
                    currentTask: '',
                    currentIndex: 0,
                    totalTasks: tasksToProcess.length
                });

                for (let i = 0; i < tasksToProcess.length; i++) {
                    const task = tasksToProcess[i];
                    setProgressData({
                        currentTask: `${quickWinTypeLabel}: ${task.name}`,
                        currentIndex: i + 1,
                        totalTasks: tasksToProcess.length
                    });
                    const newTask = await createQuickWinAdCopy(task.task_id);
                    createdTaskIds.push(newTask.task_id);
                    createdCount++;
                }

                setShowProgress(false);
            } else if (selectedQuickWin === 'keyword_research') {
                // Filter out articles that already have keyword research quick wins
                const existingQuickWins = tasks.filter(t =>
                    t.input_data &&
                    typeof t.input_data === 'object' &&
                    'quick_win_type' in t.input_data &&
                    (t.input_data as any).quick_win_type === 'keyword_research'
                );

                const sourceTasksWithQuickWins = new Set(
                    existingQuickWins.map(qw => (qw.input_data as any).source_task_id)
                );

                const tasksToProcess = completedTasks.filter(
                    task => !sourceTasksWithQuickWins.has(task.task_id)
                );

                // Show progress modal
                setShowProgress(true);
                setProgressData({
                    currentTask: '',
                    currentIndex: 0,
                    totalTasks: tasksToProcess.length
                });

                for (let i = 0; i < tasksToProcess.length; i++) {
                    const task = tasksToProcess[i];
                    setProgressData({
                        currentTask: `${quickWinTypeLabel}: ${task.name}`,
                        currentIndex: i + 1,
                        totalTasks: tasksToProcess.length
                    });
                    const newTask = await createQuickWinKeywordResearch(task.task_id);
                    createdTaskIds.push(newTask.task_id);
                    createdCount++;
                }

                setShowProgress(false);
            } else if (selectedQuickWin === 'email_newsletter') {
                // Ask user if they want batch mode
                const useBatchMode = window.confirm(
                    `Generate email newsletters in batch mode?\n\n` +
                    `• YES: Auto-generate multiple newsletters (4 articles each) using all completed articles with round-robin selection across streams\n` +
                    `• NO: Create a single newsletter from the first 5 articles`
                );

                if (useBatchMode) {
                    // Batch mode: auto-generate multiple newsletters
                    const response = await createBatchNewsletters(selectedProjectId, 'Weekly Digest', undefined, 4);
                    createdTaskIds.push(...response.task_ids);
                    createdCount = response.newsletters_created;
                } else {
                    // Single mode: use first 5 articles
                    const taskIds = completedTasks.slice(0, 5).map(t => t.task_id);
                    const newTask = await createQuickWinEmailNewsletter(selectedProjectId, taskIds);
                    createdTaskIds.push(newTask.task_id);
                    createdCount = 1;
                }
            } else if (selectedQuickWin === 'content_calendar') {
                // Ask user if they want round-robin mode
                const useRoundRobin = window.confirm(
                    `Generate content calendar with round-robin selection?\n\n` +
                    `• YES: Use round-robin selection across all streams for balanced, diverse content scheduling\n` +
                    `• NO: Use linear approach with all selected articles in order`
                );

                if (useRoundRobin) {
                    // Round-robin mode: auto-select articles from all streams
                    const newTask = await createQuickWinContentCalendar(selectedProjectId, [], 'weekly', undefined, true);
                    createdTaskIds.push(newTask.task_id);
                    createdCount = 1;
                } else {
                    // Linear mode: use all selected articles
                    const taskIds = completedTasks.map(t => t.task_id);
                    const newTask = await createQuickWinContentCalendar(selectedProjectId, taskIds);
                    createdTaskIds.push(newTask.task_id);
                    createdCount = 1;
                }
            }

            // Add message to backend and local state with task IDs
            if (currentConversationId && createdTaskIds.length > 0) {
                const messageContent = `✓ Created ${createdCount} ${quickWinTypeLabel} task${createdCount !== 1 ? 's' : ''}! Ready to execute.`;

                // Store task IDs in conversation metadata by creating a special backend message
                try {
                    const response = await fetch(`${API_BASE_URL}/conversations/${currentConversationId}/messages`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            conversation_id: currentConversationId,
                            role: 'assistant',
                            content: messageContent,
                            metadata: { tasks_created: createdTaskIds }
                        })
                    });

                    if (response.ok) {
                        const savedMessage = await response.json();
                        // Add to local state
                        setMessages(prev => [...prev, savedMessage]);
                    }
                } catch (err) {
                    console.error('Failed to save message:', err);
                    // Fallback to local state only
                    setMessages(prev => [...prev, {
                        message_id: `quick-win-${Date.now()}`,
                        conversation_id: currentConversationId,
                        role: 'assistant' as const,
                        content: messageContent,
                        created_at: new Date().toISOString(),
                        metadata: {
                            tasks_created: createdTaskIds
                        }
                    }]);
                }
            }

            setSelectedQuickWin('');
            setSelectedStreams([]);
            setShowStreamSelector(false);

            // Refresh quick wins count to reflect newly created tasks
            await loadQuickWinsCount();

            // Check for pending quick wins to show the "Continue Previous Generation" prompt
            await checkPendingQuickWins();
        } catch (error) {
            console.error('Failed to generate quick win:', error);
            alert('Failed to generate quick win tasks.');
        } finally {
            setGeneratingQuickWin(false);
        }
    };

    const handleToggleStream = (stream: string) => {
        setSelectedStreams(prev => {
            if (prev.includes(stream)) {
                return prev.filter(s => s !== stream);
            } else {
                return [...prev, stream];
            }
        });
    };

    const handleSelectAllStreams = () => {
        if (selectedStreams.length === masterplanStreams.length) {
            setSelectedStreams([]);
        } else {
            setSelectedStreams([...masterplanStreams]);
        }
    };

    const handleGenerateStream = async (streamName: string) => {
        if (!selectedProjectId || isLoading) return;

        const message = streamName === 'ALL_ARTICLES'
            ? 'generate all articles'
            : `generate articles for ${streamName}`;

        setInputValue(message);
        // Wait a tick for state to update, then trigger send
        setTimeout(() => {
            handleSendMessage();
        }, 0);
    };

    // Extract stream names from message content
    const extractStreamsFromMessage = (content: string): string[] => {
        const streams: string[] = [];

        // Look for the pattern where streams are listed after "Content Streams Found:"
        const streamSectionMatch = content.match(/Content Streams Found:[\s\S]*?(?=\n\n|\*\*What's next\?\*\*|$)/);

        if (streamSectionMatch) {
            // Extract bullet points with stream names
            const bulletMatches = Array.from(streamSectionMatch[0].matchAll(/•\s*(.+?)(?:\s*\(|$)/g));
            for (const match of bulletMatches) {
                const streamName = match[1].trim();
                if (streamName) {
                    streams.push(streamName);
                }
            }
        }

        return streams;
    };

    // Check if message is asking user to generate articles
    const shouldShowStreamButtons = (content: string): boolean => {
        return content.includes("I've analyzed your document") &&
               content.includes("generate all articles");
    };

    // Render message content with inline stream buttons
    const handleWordCountSelect = (selection: string) => {
        if (isLoading) return;
        setInputValue(selection);
        setTimeout(() => { handleSendMessage(); }, 0);
    };

    const [repurposeDismissed, setRepurposeDismissed] = useState<Set<string>>(new Set());
    const [repurposingFromChat, setRepurposingFromChat] = useState(false);

    // Per-quick-win-type label + module name + catalogue route
    const TYPE_META: Record<string, { singular: string; plural: string; module: string; path: string }> = {
        article:            { singular: 'Article',         plural: 'Articles',          module: 'Articles by Stream',   path: '/catalogue/articles' },
        email_newsletter:   { singular: 'Email',           plural: 'Emails',            module: 'Emails & Newsletters', path: '/catalogue/newsletters' },
        social_media_posts: { singular: 'Social Media Post', plural: 'Social Media Posts', module: 'Social Media Posts',   path: '/catalogue/social' },
        social_media:       { singular: 'Social Media Post', plural: 'Social Media Posts', module: 'Social Media Posts',   path: '/catalogue/social' },
        meta_data:          { singular: 'SEO Metadata',   plural: 'SEO Metadata',      module: 'SEO Metadata',         path: '/catalogue/seo' },
        ad_copy:            { singular: 'Ad Copy',         plural: 'Ad Copy',           module: 'Social Media Posts',   path: '/catalogue/social' },
        keyword_research:   { singular: 'Keyword Research', plural: 'Keyword Research', module: 'Articles by Stream',   path: '/catalogue/articles' },
        content_calendar:   { singular: 'Content Calendar', plural: 'Content Calendars', module: 'Content Calendars',   path: '/catalogue/calendars' },
        faq_block:          { singular: 'FAQ Block',        plural: 'FAQ Blocks',        module: 'FAQs',                 path: '/catalogue?tab=quick_wins&qw=faqs' },
        content_block:      { singular: 'Content Block',    plural: 'Content Blocks',    module: 'Content Blocks',       path: '/catalogue?tab=quick_wins&qw=blocks' },
    };

    const renderGenerationComplete = (payload: { type: string; tasks: Array<{ task_id: string; task_name: string }> }) => {
        const meta = TYPE_META[payload.type] || TYPE_META.article;
        const count = payload.tasks.length;
        const noun = count === 1 ? meta.singular : `${count} ${meta.plural}`;
        return (
            <div className="message-text">
                <p style={{ margin: 0 }}>
                    {noun} generated. Visit{' '}
                    <a
                        href={meta.path}
                        onClick={(e) => { e.preventDefault(); navigate(meta.path); }}
                        style={{ color: 'var(--primary-500)', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {meta.module}
                    </a>{' '}
                    in the menu to view {count === 1 ? 'it' : 'them'}.
                </p>
                {payload.tasks.length > 0 && (
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                        {payload.tasks.map(t => (
                            <li key={t.task_id} style={{ marginBottom: 4 }}>
                                <a
                                    href={`/preview/${t.task_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--primary-500)', textDecoration: 'underline' }}
                                >
                                    {t.task_name}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const renderMessageContent = (content: string, showButtons: boolean) => {
        // Generation-complete summary (one per type group)
        if (content.startsWith('__GENERATION_COMPLETE__:')) {
            try {
                const payload = JSON.parse(content.slice('__GENERATION_COMPLETE__:'.length));
                return renderGenerationComplete(payload);
            } catch {
                return <div className="message-text">{content}</div>;
            }
        }

        // Repurpose suggestion card
        if (content.startsWith('__REPURPOSE_SUGGESTION__:')) {
            const payload = content.substring('__REPURPOSE_SUGGESTION__:'.length);
            const [projId, idsCsv] = payload.split('|');
            // Article IDs generated in this chat turn. We repurpose ONLY these — not
            // every article in the project (see GENERATION_COMPLETE handler).
            const targetArticleIds = (idsCsv || '').split(',').map(s => s.trim()).filter(Boolean);
            if (repurposeDismissed.has(projId)) return null;
            // Legacy suggestion messages (pre-fix) carried no task IDs — without a
            // target list we can't safely repurpose, so don't offer the runaway action.
            if (targetArticleIds.length === 0) return null;
            return (
                <div className="message-text">
                    <div className="repurpose-suggestion-card">
                        <p>Articles generated! Repurpose them for social media, email sequences, and more?</p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <GlassButton
                                variant="primary"
                                disabled={repurposingFromChat}
                                onClick={async () => {
                                    setRepurposingFromChat(true);
                                    try {
                                        let count = 0;
                                        for (const taskId of targetArticleIds) {
                                            try {
                                                await repurposeArticle({ task_id: taskId, project_id: projId });
                                                count++;
                                            } catch { /* skip failed */ }
                                        }
                                        setMessages(prev => [...prev, {
                                            message_id: `repurpose-done-${Date.now()}`,
                                            conversation_id: currentConversationId!,
                                            role: 'assistant' as const,
                                            content: `Repurposed ${count} article${count !== 1 ? 's' : ''} for all channels. Check the Content Catalogue for your new social posts, carousels, video scripts, pull quotes, and email drip sequences.`,
                                            created_at: new Date().toISOString()
                                        }]);
                                    } catch { /* ignore */ }
                                    setRepurposingFromChat(false);
                                    setRepurposeDismissed(prev => new Set(prev).add(projId));
                                }}
                            >
                                {repurposingFromChat ? 'Repurposing...' : 'Repurpose Now'}
                            </GlassButton>
                            <GlassButton
                                variant="secondary"
                                onClick={() => setRepurposeDismissed(prev => new Set(prev).add(projId))}
                            >
                                Not Now
                            </GlassButton>
                        </div>
                    </div>
                </div>
            );
        }
        // Word count selection buttons
        if (content.includes('Please select a word count')) {
            const wordCountOptions = [
                { label: '250\u2013500 words', sublabel: 'Short-form, concise overview', value: '250-500' },
                { label: '500\u20131,000 words', sublabel: 'Standard article, key points covered', value: '500-1000' },
                { label: '1,000\u20131,500 words', sublabel: 'In-depth article, comprehensive coverage', value: '1000-1500' },
                { label: '1,500\u20132,000 words', sublabel: 'Long-form, detailed exploration', value: '1500-2000' },
            ];
            const parts = content.split(/Please select a word count[^\n]*/);
            return (
                <div className="message-text">
                    {parts[0] && parts[0].trim() && (
                        <div style={{ marginBottom: '12px', whiteSpace: 'pre-wrap' }}>{parts[0].trim()}</div>
                    )}
                    <div style={{ marginBottom: '8px', fontWeight: 600 }}>Please select a word count for the article(s):</div>
                    <div className="word-count-options">
                        {wordCountOptions.map(opt => (
                            <button
                                key={opt.value}
                                className="word-count-btn"
                                onClick={() => handleWordCountSelect(opt.value)}
                                disabled={isLoading}
                            >
                                <span className="word-count-label">{opt.label}</span>
                                <span className="word-count-sublabel">{opt.sublabel}</span>
                            </button>
                        ))}
                    </div>
                </div>
            );
        }

        if (!showButtons) {
            return <div className="message-text">{content}</div>;
        }

        // Split message into parts and add buttons next to stream names
        const lines = content.split('\n');
        const renderedLines: React.ReactElement[] = [];

        lines.forEach((line, index) => {
            // Check if this line is "• 1)" or "• 2)" etc (bullet + number on same line)
            const bulletNumberMatch = line.match(/^\s*•\s*(\d+)\)\s*$/);

            if (bulletNumberMatch && index + 1 < lines.length) {
                // This is "• 1)" - next line should have the stream name
                const nextLine = lines[index + 1];
                const streamName = nextLine.trim();
                const number = bulletNumberMatch[1];

                if (streamName && !nextLine.match(/^\s*•\s*\d+\)\s*$/)) {
                    // Skip rendering this line, it will be rendered with the stream name
                    return;
                } else {
                    renderedLines.push(<div key={index}>{line}</div>);
                }
            } else if (index > 0 && lines[index - 1].match(/^\s*•\s*(\d+)\)\s*$/)) {
                // This line follows "• 1)" - treat it as a stream name
                const streamName = line.trim();
                const numberLine = lines[index - 1].match(/^\s*•\s*(\d+)\)\s*$/);

                if (numberLine && streamName) {
                    renderedLines.push(
                        <div key={index} className="stream-line">
                            <span className="stream-text">• {numberLine[1]}) {streamName}</span>
                            <button
                                onClick={() => handleGenerateStream(streamName)}
                                className="stream-inline-btn"
                                disabled={isLoading}
                                title={`Generate articles for ${streamName}`}
                            >
                                Generate {streamName}
                            </button>
                        </div>
                    );
                } else {
                    renderedLines.push(<div key={index}>{line}</div>);
                }
            } else {
                renderedLines.push(<div key={index}>{line}</div>);
            }
        });

        return <div className="message-text">{renderedLines}</div>;
    };

    const handleNewChat = () => {
        setMessages([]);
        setCurrentConversationId(undefined);
        navigate('/chat');
    };

    const handleSelectConversation = (convId: string) => {
        setCurrentConversationId(convId);
        navigate(`/chat/${convId}`);
    };

    const handleStartEditConversation = (convId: string, currentTitle: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent selecting the conversation when clicking edit
        setEditingConversationId(convId);
        setEditingTitle(currentTitle);
    };

    const handleSaveConversationTitle = async (convId: string) => {
        if (!editingTitle.trim()) {
            setEditingConversationId(null);
            return;
        }

        try {
            await updateConversation(convId, editingTitle.trim());
            await loadConversations();
            setEditingConversationId(null);
        } catch (error) {
            console.error('Failed to update conversation title:', error);
            alert('Failed to update conversation title');
        }
    };

    const handleCancelEdit = () => {
        setEditingConversationId(null);
        setEditingTitle('');
    };

    const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, convId: string) => {
        if (e.key === 'Enter') {
            handleSaveConversationTitle(convId);
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    const handleCreateProject = async (name: string, description: string) => {
        try {
            const newProject = await createProject({
                name,
                description: description || undefined
            });
            await loadProjects();
            setSelectedProjectId(newProject.project_id);
            setShowCreateProjectModal(false);
            alert(`Project "${name}" created successfully!`);
        } catch (error) {
            console.error('Failed to create project:', error);
            alert('Failed to create project');
        }
    };

    const handleStartEditProjectName = () => {
        if (!selectedProjectId) return;
        const project = projects.find(p => p.project_id === selectedProjectId);
        if (!project) return;

        setProjectNameValue(project.name);
        setEditingProjectName(true);
    };

    const handleSaveProjectName = async () => {
        if (!selectedProjectId || !projectNameValue.trim()) return;

        try {
            await updateProject(selectedProjectId, {
                name: projectNameValue.trim()
            });
            await loadProjects();
            setEditingProjectName(false);
        } catch (error) {
            console.error('Failed to update project name:', error);
            alert('Failed to update project name');
        }
    };

    const handleCancelEditProjectName = () => {
        setEditingProjectName(false);
        setProjectNameValue('');
    };

    const handleProjectNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSaveProjectName();
        } else if (e.key === 'Escape') {
            handleCancelEditProjectName();
        }
    };

    const handleStartEditProjectDescription = () => {
        if (!selectedProjectId) return;
        const project = projects.find(p => p.project_id === selectedProjectId);
        if (!project) return;

        setProjectDescriptionValue(project.description || '');
        setEditingProjectDescription(true);
    };

    const handleSaveProjectDescription = async () => {
        if (!selectedProjectId) return;

        try {
            await updateProject(selectedProjectId, {
                description: projectDescriptionValue.trim() || undefined
            });
            await loadProjects();
            setEditingProjectDescription(false);
        } catch (error) {
            console.error('Failed to update project description:', error);
            alert('Failed to update project description');
        }
    };

    const handleCancelEditProjectDescription = () => {
        setEditingProjectDescription(false);
        setProjectDescriptionValue('');
    };

    const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            // Cmd+Enter or Ctrl+Enter to save
            handleSaveProjectDescription();
        } else if (e.key === 'Escape') {
            handleCancelEditProjectDescription();
        }
    };

    const handleDeleteConversation = async (convId: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent selecting the conversation when clicking delete

        const conversation = conversations.find(c => c.conversation_id === convId);
        const title = conversation?.title || `Chat ${new Date(conversation?.created_at || '').toLocaleDateString()}`;

        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
            return;
        }

        try {
            await deleteConversation(convId);

            // If we're currently viewing this conversation, navigate to new chat
            if (currentConversationId === convId) {
                handleNewChat();
            }

            // Reload conversations list
            await loadConversations();
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            alert('Failed to delete conversation');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="chat-interface-wrapper">
        <div className="chat-container">
            {/* Sidebar - Hidden: using global sidebar from Layout */}
            <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`} style={{display: 'none'}}>
                <div className="sidebar-header">
                    <h2>AiMe eContent Manager</h2>
                </div>

                {sidebarOpen && (
                    <>
                        <div className="project-selector">
                            <div className="project-selector-header">
                                <label>Project:</label>
                                <div className="project-actions">
                                    <button
                                        onClick={() => setShowCreateProjectModal(true)}
                                        className="project-action-btn create-btn"
                                        title="Create new project"
                                    >
                                        +
                                    </button>
                                    {selectedProjectId && (
                                        <>
                                            <button
                                                onClick={handleStartEditProjectName}
                                                className="project-action-btn edit-btn"
                                                title="Edit project name"
                                            >
                                                ✏️
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {editingProjectName ? (
                                <div className="project-name-edit">
                                    <input
                                        ref={projectNameInputRef}
                                        type="text"
                                        className="project-name-input"
                                        value={projectNameValue}
                                        onChange={(e) => setProjectNameValue(e.target.value)}
                                        onKeyDown={handleProjectNameKeyDown}
                                        onBlur={handleSaveProjectName}
                                        placeholder="Project name..."
                                    />
                                </div>
                            ) : (
                                <select
                                    value={selectedProjectId || ''}
                                    onChange={(e) => setSelectedProjectId(e.target.value || undefined)}
                                >
                                    <option value="">No Project</option>
                                    {projects.map(project => (
                                        <option key={project.project_id} value={project.project_id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {selectedProjectId && (() => {
                                const project = projects.find(p => p.project_id === selectedProjectId);
                                if (!project) return null;

                                return (
                                    <div className="project-description-container">
                                        {editingProjectDescription ? (
                                            <textarea
                                                ref={descriptionInputRef}
                                                className="project-description-input"
                                                value={projectDescriptionValue}
                                                onChange={(e) => setProjectDescriptionValue(e.target.value)}
                                                onKeyDown={handleDescriptionKeyDown}
                                                onBlur={handleSaveProjectDescription}
                                                placeholder="Add project description..."
                                                rows={2}
                                            />
                                        ) : (
                                            <div
                                                className="project-description"
                                                onClick={handleStartEditProjectDescription}
                                            >
                                                <span className="description-icon">📄</span>
                                                <span className="description-text">
                                                    {project.description || 'Click to add description...'}
                                                </span>
                                                <button
                                                    className="description-edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStartEditProjectDescription();
                                                    }}
                                                    title="Edit description"
                                                >
                                                    ✏️
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {selectedProjectId && articlesCount > 0 && (
                                <>
                                    <div className="export-section">
                                        <label>Export Format:</label>
                                        <select
                                            value={exportFormat}
                                            onChange={(e) => setExportFormat(e.target.value as 'markdown' | 'html')}
                                            className="export-format-select"
                                        >
                                            <option value="markdown">Markdown (.md)</option>
                                            <option value="html">HTML (.html)</option>
                                        </select>
                                        <label className="metadata-checkbox-label" style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                                            <input
                                                type="checkbox"
                                                checked={includeMetadata}
                                                onChange={(e) => setIncludeMetadata(e.target.checked)}
                                            />
                                            Include metadata footer
                                        </label>
                                        <button
                                            onClick={handleExportArticles}
                                            className="export-btn"
                                            title={`Export ${articlesCount} article${articlesCount !== 1 ? 's' : ''} for this project`}
                                        >
                                            📥 Export ({articlesCount})
                                        </button>
                                    </div>

                                    <div className="image-selection-section">
                                        <label>🖼️ Hero Images</label>
                                        <button
                                            onClick={handleSelectImagesForAll}
                                            className="export-btn"
                                            disabled={selectingImages}
                                            title="Select AI-analyzed hero images for all articles without images"
                                        >
                                            {selectingImages
                                                ? `⏳ Selecting... (${imageSelectionProgress.current}/${imageSelectionProgress.total})`
                                                : '🎨 Select Images for All Articles'}
                                        </button>
                                    </div>

                                    {showPendingPrompt && pendingQuickWins && (
                                        <div className="pending-tasks-prompt">
                                            <div className="pending-tasks-header">
                                                <span className="warning-icon">⚠️</span>
                                                <strong>Pending Quick Win Tasks Detected</strong>
                                            </div>
                                            <p>You have {pendingQuickWins.total} pending quick win task{pendingQuickWins.total !== 1 ? 's' : ''} from a previous generation:</p>
                                            <ul className="pending-tasks-list">
                                                {Object.entries(pendingQuickWins.by_type).map(([type, info]: [string, any]) => (
                                                    <li key={type}>
                                                        <strong>{type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {info.count} task{info.count !== 1 ? 's' : ''}
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="pending-tasks-actions">
                                                <button
                                                    onClick={async () => {
                                                        setShowPendingPrompt(false);
                                                        await handleExecutePendingQuickWins();
                                                    }}
                                                    className="continue-btn"
                                                    disabled={executingTasks}
                                                >
                                                    ▶️ Continue Previous Generation
                                                </button>
                                                <button
                                                    onClick={() => setShowPendingPrompt(false)}
                                                    className="dismiss-btn"
                                                >
                                                    Dismiss
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="quick-wins-section">
                                        <label>Quick Wins:</label>
                                        <select
                                            value={selectedQuickWin}
                                            onChange={(e) => setSelectedQuickWin(e.target.value)}
                                            className="quick-win-select"
                                        >
                                            <option value="">Select Quick Win...</option>
                                            <option value="social_media">Social Media Posts</option>
                                            <option value="meta_data">SEO Metadata</option>
                                            <option value="ad_copy">Ad Copy Variations</option>
                                            <option value="keyword_research">Keyword Research</option>
                                            <option value="email_newsletter">Emails & Newsletters</option>
                                            <option value="content_calendar">Content Calendar</option>
                                        </select>

                                        {hasMasterplan && masterplanStreams.length > 0 && selectedQuickWin && (
                                            <button
                                                onClick={() => setShowStreamSelector(!showStreamSelector)}
                                                className="stream-filter-btn"
                                                title="Filter by content stream"
                                            >
                                                {showStreamSelector ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Streams {selectedStreams.length > 0 ? `(${selectedStreams.length})` : '(All)'}
                                            </button>
                                        )}

                                        <button
                                            onClick={handleGenerateQuickWin}
                                            className="quick-win-btn"
                                            disabled={!selectedQuickWin || generatingQuickWin}
                                        >
                                            {generatingQuickWin ? 'Generating...' : 'Generate'}
                                        </button>

                                        {showStreamSelector && hasMasterplan && masterplanStreams.length > 0 && (
                                            <div className="stream-selector">
                                                <div className="stream-selector-header">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStreams.length === masterplanStreams.length}
                                                            onChange={handleSelectAllStreams}
                                                        />
                                                        <strong>All Streams</strong>
                                                    </label>
                                                </div>
                                                <div className="stream-checkboxes">
                                                    {masterplanStreams.map((stream) => {
                                                        const cleanStream = stream.replace(/^\d+\)\s*/, '');
                                                        return (
                                                            <label key={stream} className="stream-checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedStreams.includes(stream)}
                                                                    onChange={() => handleToggleStream(stream)}
                                                                />
                                                                {cleanStream}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {selectedProjectId && totalQuickWinsCount > 0 && (
                                        <div className="export-section quick-wins-export-section">
                                            <label>Quick Wins Export:</label>
                                            <select
                                                value={quickWinExportType}
                                                onChange={(e) => setQuickWinExportType(e.target.value)}
                                                className="export-type-select"
                                            >
                                                <option value="all">All Types ({totalQuickWinsCount})</option>
                                                <option value="social_media">Social Media Posts</option>
                                                <option value="meta_data">SEO Metadata</option>
                                                <option value="ad_copy">Ad Copy</option>
                                                <option value="keyword_research">Keyword Research</option>
                                                <option value="email_newsletter">Emails & Newsletters</option>
                                                <option value="content_calendar">Content Calendars</option>
                                            </select>
                                            <select
                                                value={quickWinExportFormat}
                                                onChange={(e) => setQuickWinExportFormat(e.target.value as 'markdown' | 'html')}
                                                className="export-format-select"
                                            >
                                                <option value="markdown">Markdown (.md)</option>
                                                <option value="html">HTML (.html)</option>
                                            </select>
                                            <label className="metadata-checkbox-label" style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={includeMetadata}
                                                    onChange={(e) => setIncludeMetadata(e.target.checked)}
                                                />
                                                Include metadata footer
                                            </label>
                                            <button
                                                onClick={handleExportQuickWins}
                                                className="export-btn"
                                                disabled={quickWinsCount === 0}
                                                title={quickWinsCount === 0 ? 'No quick wins of this type available' : `Export ${quickWinsCount} quick win${quickWinsCount !== 1 ? 's' : ''}`}
                                            >
                                                📥 Export Quick Wins ({quickWinsCount})
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {selectedProjectId && (
                                <div className="landing-pages-section">
                                    <label>🚀 Landing Pages</label>
                                    <div className="landing-pages-controls">
                                        <button
                                            onClick={() => {
                                                setLandingPageModalMode('create');
                                                setShowLandingPageModal(true);
                                            }}
                                            className="export-btn"
                                            title="Create a new AI-generated landing page"
                                        >
                                            + Create Landing Page
                                        </button>
                                        {landingPagesCount > 0 && (
                                            <button
                                                onClick={() => {
                                                    setLandingPageModalMode('list');
                                                    setShowLandingPageModal(true);
                                                }}
                                                className="quick-win-btn"
                                                title={`View ${landingPagesCount} landing page${landingPagesCount !== 1 ? 's' : ''}`}
                                            >
                                                📄 View All ({landingPagesCount})
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedProjectId && (
                                <div className="landing-pages-section">
                                    <label>🎨 Brand Guides</label>
                                    <div className="landing-pages-controls">
                                        <button
                                            onClick={() => {
                                                setBrandGuideModalMode('create');
                                                setShowBrandGuideModal(true);
                                            }}
                                            className="export-btn"
                                            title="Create a new brand guide"
                                        >
                                            + Create Brand Guide
                                        </button>
                                        {brandGuidesCount > 0 && (
                                            <button
                                                onClick={() => {
                                                    setBrandGuideModalMode('list');
                                                    setShowBrandGuideModal(true);
                                                }}
                                                className="quick-win-btn"
                                                title={`View ${brandGuidesCount} brand guide${brandGuidesCount !== 1 ? 's' : ''}`}
                                            >
                                                📋 View All ({brandGuidesCount})
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        <button onClick={handleNewChat} className="new-chat-btn">
                            + New Chat
                        </button>

                        <div className="conversations-list">
                            <h3>Recent Conversations</h3>
                            {conversations
                                .filter(conv => {
                                    // If no project selected, show conversations without a project
                                    if (!selectedProjectId) {
                                        return !conv.project_id;
                                    }
                                    // If project selected, only show conversations for that project
                                    return conv.project_id === selectedProjectId;
                                })
                                .map(conv => {
                                const isEditing = editingConversationId === conv.conversation_id;
                                const displayTitle = conv.title || `Chat ${new Date(conv.created_at).toLocaleDateString()}`;

                                return (
                                    <div
                                        key={conv.conversation_id}
                                        className={`conversation-item ${conv.conversation_id === currentConversationId ? 'active' : ''}`}
                                        onClick={() => !isEditing && handleSelectConversation(conv.conversation_id)}
                                    >
                                        <div className="conv-content">
                                            {isEditing ? (
                                                <input
                                                    ref={editInputRef}
                                                    type="text"
                                                    className="conv-title-input"
                                                    value={editingTitle}
                                                    onChange={(e) => setEditingTitle(e.target.value)}
                                                    onKeyDown={(e) => handleEditKeyDown(e, conv.conversation_id)}
                                                    onBlur={() => handleSaveConversationTitle(conv.conversation_id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <>
                                                    <div className="conv-title">
                                                        {displayTitle}
                                                    </div>
                                                    <div className="conv-date">
                                                        {new Date(conv.updated_at).toLocaleString()}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {!isEditing && (
                                            <div className="conv-actions">
                                                <button
                                                    className="edit-conversation-btn"
                                                    onClick={(e) => handleStartEditConversation(conv.conversation_id, displayTitle, e)}
                                                    title="Rename conversation"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="delete-conversation-btn"
                                                    onClick={(e) => handleDeleteConversation(conv.conversation_id, e)}
                                                    title="Delete conversation"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Main Chat Area */}
            <div className="chat-main">
                {/* Collapsible project selector header */}
                <div className={`project-header-bar ${projectSelectorOpen ? 'open' : ''}`}>
                    <button
                        className="project-header-toggle"
                        onClick={() => setProjectSelectorOpen(o => !o)}
                    >
                        <FolderOpen size={16} />
                        <span className="project-header-label">
                            {selectedProjectId
                                ? projects.find(p => p.project_id === selectedProjectId)?.name ?? 'Open File'
                                : 'Open File'}
                        </span>
                        {projectSelectorOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {projectSelectorOpen && (
                        <div className="project-header-panel">
                            <select
                                value={selectedProjectId || ''}
                                onChange={(e) => {
                                    setSelectedProjectId(e.target.value || undefined);
                                    setProjectSelectorOpen(false);
                                }}
                            >
                                <option value="">No Project</option>
                                {projects.map(project => (
                                    <option key={project.project_id} value={project.project_id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="project-action-btn create-btn"
                                onClick={() => { setShowCreateProjectModal(true); setProjectSelectorOpen(false); }}
                                title="Create new project"
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Floating Toggle Button - Hidden: using global sidebar */}

                {messages.length === 0 ? (
                    <div className="welcome-screen">
                        <h1>AiMe Content Manager</h1>
                        <p>Your intelligent assistant for content strategy, SEO, and marketing tasks.</p>
                        <div className="suggestions">
                            <div className="suggestion" onClick={() => setInputValue('Help me create a content strategy for my business')}>
                                Create a content strategy
                            </div>
                            <div className="suggestion" onClick={() => setInputValue('Analyze my document and suggest articles')}>
                                Analyze document
                            </div>
                            <div className="suggestion" onClick={() => setInputValue('Generate 5 blog post ideas about [topic]')}>
                                Generate blog ideas
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="messages-container" ref={messagesContainerRef}>
                        {messages.map((message, index) => {
                            const showStreamButtons = message.role === 'assistant' && shouldShowStreamButtons(message.content);

                            return (
                                <div key={message.message_id || index} className={`message ${message.role}`}>
                                    <div className="message-avatar">
                                        {message.role === 'user' ? <User size={18} /> : <Bot size={18} />}
                                    </div>
                                    <div className="message-content">
                                        {renderMessageContent(message.content, showStreamButtons)}
                                        {message.metadata?.tasks_created && message.metadata.tasks_created.length > 0 && (
                                            <div className="tasks-info">
                                                <button onClick={handleExecuteTasks} disabled={executingTasks} className="execute-btn">
                                                    {executingTasks ? 'Executing...' : 'Execute Tasks'}
                                                </button>
                                            </div>
                                        )}
                                        {showStreamButtons && (
                                            <div className="stream-generation-inline">
                                                <button
                                                    onClick={() => handleGenerateStream('ALL_ARTICLES')}
                                                    className="stream-btn stream-btn-all"
                                                    disabled={isLoading}
                                                >
                                                    📝 Generate All Articles
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {isLoading && (
                            <div className="message assistant">
                                <div className="message-avatar"><Bot size={18} /></div>
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <span></span><span></span><span></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Image Selection Progress */}
                        {selectingImages && (
                            <>
                            {console.log('[DEBUG] Rendering progress meter - selectingImages:', selectingImages, 'progress:', imageSelectionProgress)}
                            <div className="message assistant">
                                <div className="message-avatar">🖼️</div>
                                <div className="message-content">
                                    <div className="image-selection-progress">
                                        <div className="progress-header">
                                            <div className="progress-text">
                                                <strong>🎨 Selecting Hero Images</strong>
                                                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                                                    Processing {imageSelectionProgress.current + 1} of {imageSelectionProgress.total}
                                                </span>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    setCancelImageSelection(true);
                                                    try {
                                                        await fetch(`${API_BASE_URL}/cancel_image_selection`, { method: 'POST' });
                                                    } catch (error) {
                                                        console.error('Failed to cancel:', error);
                                                    }
                                                }}
                                                className="cancel-progress-btn"
                                                title="Cancel image selection"
                                            >
                                                ✕ Cancel
                                            </button>
                                        </div>
                                        <div className="progress-bar-container">
                                            <div
                                                className="progress-bar-fill"
                                                style={{
                                                    width: `${(imageSelectionProgress.current / imageSelectionProgress.total) * 100}%`
                                                }}
                                            />
                                        </div>
                                        <div className="progress-status">
                                            {imageSelectionProgress.currentArticle && (
                                                <div className="current-article">
                                                    📄 {imageSelectionProgress.currentArticle}
                                                </div>
                                            )}
                                            <div className="progress-steps">
                                                🔍 Generating search query → 🖼️ Searching Pexels → Analyzing with Gemini AI → 💾 Saving
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}

                {/* Input Area */}
                <div className="input-container">
                    {selectedFile && (
                        <div className="file-preview">
                            <span className="file-name"><Paperclip size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {selectedFile.name}</span>
                            <button onClick={handleRemoveFile} className="remove-file-btn">✕</button>
                        </div>
                    )}
                    <div className="grounding-toggle-row">
                        <button
                            type="button"
                            className={`grounding-toggle-btn${groundingEnabled ? ' grounding-toggle-btn--active' : ''}`}
                            onClick={() => setGroundingEnabled(prev => !prev)}
                            title={groundingEnabled ? 'Live research ON — article will search current web sources' : 'Live research OFF — click to enable'}
                        >
                            <Globe size={14} />
                            Research live sources
                        </button>
                    </div>
                    <div className="input-row">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept=".txt,.html,.htm,.md,.docx,.pdf"
                            style={{ display: 'none' }}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="attach-btn"
                            title="Attach file"
                            disabled={isLoading}
                        >
                            <Paperclip size={18} />
                        </button>
                        <textarea
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Message AiMe"
                            rows={1}
                            disabled={isLoading}
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={(!inputValue.trim() && !selectedFile) || isLoading}
                            className="send-btn"
                        >
                            ➤
                        </button>
                    </div>
                </div>
            </div>

            {/* Progress Modal */}
            <ProgressModal
                isOpen={showProgress}
                currentTask={progressData.currentTask}
                currentIndex={progressData.currentIndex}
                totalTasks={progressData.totalTasks}
                taskType={currentTaskType}
                quickWinType={currentQuickWinType}
            />

            {/* Create Project Modal */}
            <CreateProjectModal
                isOpen={showCreateProjectModal}
                onClose={() => setShowCreateProjectModal(false)}
                onCreate={handleCreateProject}
            />

            {/* Masterplan Wizard */}
            {showMasterplanWizard && selectedProjectId && (
                <MasterplanWizard
                    projectId={selectedProjectId}
                    onClose={() => setShowMasterplanWizard(false)}
                    onComplete={(masterplanId) => {
                        setShowMasterplanWizard(false);
                        // Masterplan created - user can now proceed with stream generation
                        console.log('Masterplan created:', masterplanId);
                    }}
                />
            )}

            {/* Landing Page Modal */}
            <LandingPageModal
                isOpen={showLandingPageModal}
                mode={landingPageModalMode}
                projectId={selectedProjectId}
                onClose={() => setShowLandingPageModal(false)}
                onSuccess={() => {
                    loadLandingPagesCount();
                }}
            />

            {/* Brand Guide Modals */}
            {showBrandGuideModal && brandGuideModalMode === 'create' && selectedProjectId && (
                <BrandGuideCreator
                    projectId={selectedProjectId}
                    onClose={() => setShowBrandGuideModal(false)}
                    onSuccess={() => {
                        loadBrandGuidesCount();
                        setShowBrandGuideModal(false);
                    }}
                />
            )}

            {showBrandGuideModal && brandGuideModalMode === 'list' && selectedProjectId && (
                <div className="modal-overlay" onClick={() => setShowBrandGuideModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Brand Guides</h2>
                            <button className="close-btn" onClick={() => setShowBrandGuideModal(false)}>×</button>
                        </div>
                        <BrandGuidesList
                            projectId={selectedProjectId}
                            onCreate={() => {
                                setBrandGuideModalMode('create');
                            }}
                            onEdit={(guide) => {
                                // TODO: Implement edit functionality
                                console.log('Edit brand guide:', guide);
                            }}
                        />
                    </div>
                </div>
            )}
        </div>

        </div>
    );
};

export default ChatInterface;
